import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import LazyLoad from 'react-lazy-load';

import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { HORIZONTAL_OFFSET_RESP_IMG } from '../../../constants/imageConstants.js';
import { charLimitedString } from '../../helpers/charLimitedString.ts';

import { type HeroCarouselImage_item$key } from './__generated__/HeroCarouselImage_item.graphql.ts';
import { type HeroCarouselImage_sortedPhoto$key } from './__generated__/HeroCarouselImage_sortedPhoto.graphql.ts';

import { SRC_SET_WIDTHS, SIZES } from './constants.ts';
import styles from './HeroCarouselImage.module.css';

const itemFragment = graphql`
    fragment HeroCarouselImage_item on Item {
        description
    }
`;

const sortedPhotoFragment = graphql`
    fragment HeroCarouselImage_sortedPhoto on ItemPhoto {
        masterOrZoomPath
        alt
    }
`;

export const HeroCarouselImage: FC<{
    item: HeroCarouselImage_item$key;
    sortedPhoto: HeroCarouselImage_sortedPhoto$key;
    eagerLoad?: boolean;
}> = ({ item: itemRef, sortedPhoto: sortedPhotoRef, eagerLoad = false }) => {
    const item = useFragment(itemFragment, itemRef);
    const sortedPhoto = useFragment(sortedPhotoFragment, sortedPhotoRef);

    let child: ReactNode = null;

    const { masterOrZoomPath, alt } = sortedPhoto;
    const shortDescription = item.description ? charLimitedString(item.description, 250) : '';

    if (masterOrZoomPath && alt) {
        const srcSet = getSrcsetString(masterOrZoomPath, SRC_SET_WIDTHS);

        child = (
            <figure className={classnames(dibsCss.wFull, dibsCss.hFull, dibsCss.m0)}>
                <picture>
                    <img
                        src={masterOrZoomPath}
                        alt={alt}
                        className={classnames(
                            dibsCss.block,
                            dibsCss.wFull,
                            dibsCss.objectScaleDown,
                            styles.mainPhoto
                        )}
                        sizes={SIZES}
                        srcSet={srcSet}
                        {...(eagerLoad ? { fetchPriority: 'high' } : {})}
                    />
                </picture>
                <figcaption className={dibsCss.hidden}>{shortDescription}</figcaption>
            </figure>
        );

        if (!eagerLoad) {
            child = (
                <>
                    <LazyLoad
                        offset={HORIZONTAL_OFFSET_RESP_IMG}
                        className={classnames(dibsCss.wFull, dibsCss.hFull)}
                    >
                        {child}
                    </LazyLoad>
                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `<img src="${masterOrZoomPath}" alt="${alt}" />`,
                        }}
                    />
                </>
            );
        }
    }

    return child;
};
